/**
 * Detected Trips Preview Component
 * 
 * Shows trips detected from email bookings with preview and editing options.
 * Allows creating all trips or selecting individual ones.
 * Supports re-sync: matches bookings to existing trips when applicable.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  View,
  Modal,
  ScrollView,
  Pressable,
  ActivityIndicator,
  TextInput,
  Platform,
  useWindowDimensions,
  InteractionManager,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { 
  detectTripsFromEmail,
  queueTripDetection, 
  subscribeToTripDetectionTask,
  categorizeDetectedTrips, 
  type DetectionTaskStatus,
  type DetectedTrip, 
  type CategorizedDetection 
} from '@/services/tripDetection';
import { createTrip, type CreateTripData } from '@/services/trips';
import { syncInbox } from '@/services/inboxSync';
import type { InboxProviderId } from '@/services/inboxSync';
import { storeDetectedBookings } from '@/services/bookings';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore, type PremiumGatePayload } from '@/state/premiumGateStore';
import { consumeLifetimeLimit } from '@/services/usageCounters';
import { Portal } from '@gorhom/portal';
import { hasPremiumAccess } from '@/services/entitlements';
import {
  emailScanArrivalWindowForScan,
  emailOlderConfirmationsHint,
  emailScanDeeperScanTeaser,
} from '@/utils/emailScanLookbackCopy';
import { deriveMergedDetectedTripPresentation } from '@/utils/mergedDetectedTripMetadata';

type DetectedTripsPreviewProps = {
  visible: boolean;
  onClose: () => void;
  onTripsCreated: () => void;
  userId: string;
  provider?: InboxProviderId;
  showEmailSubjects?: boolean;
  /** Use `embedded` after OAuth to avoid stacking an RN Modal on an in-flight native presenter. */
  presentation?: 'modal' | 'embedded' | 'screen';
  initialTaskId?: string;
  onMinimize?: (session: { taskId: string; userId: string; provider: InboxProviderId; label: string }) => void;
};

const QUICK_SCAN_LOOKBACK_DAYS = 45;
const QUICK_SCAN_MAX_RESULTS = 60;
const FULL_SCAN_LOOKBACK_DAYS = 180;
const FULL_SCAN_MAX_RESULTS = 160;
const QUICK_SCAN_STORAGE_KEY = '@tripflow_email_detection_quick_done';

export function DetectedTripsPreview({
  visible,
  onClose,
  onTripsCreated,
  userId,
  provider = 'gmail',
  showEmailSubjects = false,
  presentation = 'modal',
  initialTaskId,
  onMinimize,
}: DetectedTripsPreviewProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const profile = useUserStore((state) => state.profile);
  const isPremium = hasPremiumAccess({
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const openPremiumGateAfterDismiss = useCallback(
    (payload: PremiumGatePayload) => {
      onClose();
      InteractionManager.runAfterInteractions(() => {
        openPremiumGate(payload);
      });
    },
    [onClose, openPremiumGate],
  );
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [trips, setTrips] = useState<DetectedTrip[]>([]);
  const [selectedTrips, setSelectedTrips] = useState<Set<number>>(new Set());
  const [editingTrip, setEditingTrip] = useState<number | null>(null);
  const [editedTrips, setEditedTrips] = useState<Map<number, Partial<DetectedTrip>>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [mergingTrips, setMergingTrips] = useState<Set<number>>(new Set());
  const [expandedTrips, setExpandedTrips] = useState<Set<number>>(new Set());
  const [createdTripIds, setCreatedTripIds] = useState<string[]>([]);
  const [creationProgress, setCreationProgress] = useState<{ current: number; total: number; tripName?: string } | null>(null);
  const [failedTrips, setFailedTrips] = useState<Array<{ trip: DetectedTrip; index: number; error: string }>>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [detectionProgress, setDetectionProgress] = useState<string | null>(null);
  const [quickScanDone, setQuickScanDone] = useState(false);
  const [lastScanWasQuick, setLastScanWasQuick] = useState(false);
  /** Lookback (days) for the current or most recent detection run; explains inbox window in UI. */
  const [scanLookbackDays, setScanLookbackDays] = useState<number | null>(null);
  
  // Re-sync state: bookings that match existing trips
  const [existingTripMatches, setExistingTripMatches] = useState<CategorizedDetection['existingTripMatches']>([]);
  const [selectedExistingMatches, setSelectedExistingMatches] = useState<Set<number>>(new Set());
  const [expandedExistingMatches, setExpandedExistingMatches] = useState<Set<number>>(new Set());

  // Users can exclude individual detected emails/bookings when they are clearly not travel-related.
  // We key by list section + parent index + booking index to keep it stable without requiring backend ids.
  const [excludedBookingKeys, setExcludedBookingKeys] = useState<Set<string>>(new Set());

  const isMountedRef = useRef(true);
  const detectionUnsubRef = useRef<null | (() => void)>(null);
  const currentTaskIdRef = useRef<string | null>(initialTaskId ?? null);
  /** Prevents re-running auto-detect when `trips` is cleared for a manual deeper scan (that used to cancel full scan). */
  const autoDetectSessionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    currentTaskIdRef.current = initialTaskId ?? null;
  }, [initialTaskId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      detectionUnsubRef.current?.();
      detectionUnsubRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!visible) {
      // If the user closes the modal, stop listening to progress to avoid state updates after unmount.
      detectionUnsubRef.current?.();
      detectionUnsubRef.current = null;
      autoDetectSessionKeyRef.current = null;
      setMinimized(false);
      setDetecting(false);
      setDetectionProgress(null);
    }
  }, [visible]);

  useEffect(() => {
    let cancelled = false;
    const loadQuickScanFlag = async () => {
      try {
        const key = `${QUICK_SCAN_STORAGE_KEY}_${userId}`;
        const stored = await AsyncStorage.getItem(key);
        if (!cancelled) {
          setQuickScanDone(stored === 'true');
        }
      } catch {
        if (!cancelled) {
          setQuickScanDone(false);
        }
      }
    };
    if (userId) {
      loadQuickScanFlag();
    }
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const detectTrips = useCallback(async (options?: { forceFullScan?: boolean }) => {
    const shouldQuickScan = !options?.forceFullScan && quickScanDone === false;
    const maxResults = shouldQuickScan ? QUICK_SCAN_MAX_RESULTS : FULL_SCAN_MAX_RESULTS;
    const lookbackDays = shouldQuickScan ? QUICK_SCAN_LOOKBACK_DAYS : FULL_SCAN_LOOKBACK_DAYS;
    setLastScanWasQuick(shouldQuickScan);
    setScanLookbackDays(lookbackDays);
    setDetecting(true);
    setMinimized(false);
    setError(null);
    setDetectionProgress(shouldQuickScan ? 'Starting a quick scan...' : 'Queuing detection task...');
    setTrips([]);
    setExistingTripMatches([]);
    setSelectedTrips(new Set());
    setSelectedExistingMatches(new Set());
    setExpandedTrips(new Set());
    setExpandedExistingMatches(new Set());
    
    try {
      // Reset any previous subscription
      detectionUnsubRef.current?.();
      detectionUnsubRef.current = null;

      const handleTaskProgress = (status: DetectionTaskStatus) => {
        if (!isMountedRef.current) return;
        // Handle progress updates
        if (status.message) {
          setDetectionProgress(status.message);
        }
        
        if (status.status === 'completed' && status.trips) {
          // Detection completed successfully
          setDetectionProgress('Categorizing trips...');
          
          categorizeDetectedTrips(status.trips, userId).then((categorized) => {
            if (!isMountedRef.current) return;
            setTrips(categorized.newTrips);
            setExistingTripMatches(categorized.existingTripMatches);
            
            // Select all new trips by default
            setSelectedTrips(new Set(categorized.newTrips.map((_, index) => index)));
            // Select existing trip matches by default only when all bookings have a location
            const preselectedExisting = categorized.existingTripMatches
              .map((match, index) => (match.bookings.every((b) => !!b.location) ? index : null))
              .filter((v): v is number => v !== null);
            setSelectedExistingMatches(new Set(preselectedExisting));
            
            console.log('[DetectedTripsPreview] Categorized results:', {
              newTrips: categorized.newTrips.length,
              existingMatches: categorized.existingTripMatches.length,
            });
            
            setDetecting(false);
            setDetectionProgress(null);
            detectionUnsubRef.current?.();
            detectionUnsubRef.current = null;
            if (shouldQuickScan) {
              const key = `${QUICK_SCAN_STORAGE_KEY}_${userId}`;
              AsyncStorage.setItem(key, 'true').catch(() => {});
              setQuickScanDone(true);
            }
          });
        } else if (status.status === 'failed') {
          // Detection failed
          setError(status.error || 'Failed to detect trips');
          setDetecting(false);
          setDetectionProgress(null);
          detectionUnsubRef.current?.();
          detectionUnsubRef.current = null;
        }
      };

      // Use async queue-based detection to avoid timeout, or resume an existing task after minimize.
      const taskTaskId = currentTaskIdRef.current;
      const { taskId, unsubscribe } = taskTaskId && !options?.forceFullScan
        ? await subscribeToTripDetectionTask(taskTaskId, handleTaskProgress)
        : await queueTripDetection(
            { provider, maxResults, lookbackDays },
            handleTaskProgress,
          );

      // If the component unmounted or the modal closed while the await was in
      // flight, the unmount/visible-change effect already ran and never saw
      // this subscription — tear it down here to avoid a leaked listener.
      if (!isMountedRef.current) {
        try {
          unsubscribe();
        } catch {
          /* already gone */
        }
        return;
      }

      currentTaskIdRef.current = taskId;
      detectionUnsubRef.current = unsubscribe;
      
      // Don't set detecting to false here - wait for completion callback
    } catch (err) {
      console.error('[DetectedTripsPreview] Error detecting trips:', err);
      setError(err instanceof Error ? err.message : 'Failed to detect trips');
      setDetecting(false);
      setDetectionProgress(null);
    }
  }, [provider, quickScanDone, userId]);

  const detectTripsRef = useRef(detectTrips);
  detectTripsRef.current = detectTrips;

  // Auto-run once per open (per userId+provider). Do NOT depend on `trips.length` — clearing the list for a deeper scan
  // must not re-trigger this effect or it would start another quick scan and race `forceFullScan`.
  useEffect(() => {
    if (!visible || !userId) return;
    const sessionKey = `${userId}:${provider}`;
    if (autoDetectSessionKeyRef.current === sessionKey) return;
    autoDetectSessionKeyRef.current = sessionKey;
    detectTripsRef.current();
  }, [visible, userId, provider]);

  // IMPORTANT: Must be stable across re-renders and filtering, otherwise toggling one row can affect another.
  // We derive a signature from booking fields instead of list indices.
  const bookingKey = useCallback(
    (scope: 'new' | 'existing', parentIndex: number, booking: any, ordinalInTrip: number = 0) => {
      const rawEmailDate =
        booking?.rawEmailDate instanceof Date
          ? booking.rawEmailDate.toISOString()
          : typeof booking?.rawEmailDate === 'string'
            ? booking.rawEmailDate
            : '';
      const departureOrCheckIn =
        booking?.departureDate || booking?.checkInDate || booking?.arrivalDate || booking?.checkOutDate || '';
      const parts = [
        scope,
        String(parentIndex),
        String(ordinalInTrip),
        (booking?.type || '').toString(),
        (booking?.provider || '').toString(),
        (booking?.confirmationNumber || '').toString(),
        (booking?.flightNumber || '').toString(),
        (booking?.origin || '').toString(),
        (booking?.destination || '').toString(),
        (booking?.location || '').toString(),
        (booking?.rawEmailSubject || '').toString(),
        departureOrCheckIn.toString(),
        rawEmailDate,
      ];
      return parts
        .join('|')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    },
    [],
  );

  const isBookingExcluded = useCallback(
    (scope: 'new' | 'existing', parentIndex: number, booking: any, ordinalInTrip: number = 0) =>
      excludedBookingKeys.has(bookingKey(scope, parentIndex, booking, ordinalInTrip)),
    [bookingKey, excludedBookingKeys],
  );

  const toggleBookingExcluded = useCallback(
    (scope: 'new' | 'existing', parentIndex: number, booking: any, ordinalInTrip: number = 0) => {
      const key = bookingKey(scope, parentIndex, booking, ordinalInTrip);
      setExcludedBookingKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [bookingKey],
  );

  const getDisplayTrip = useCallback(
    (trip: DetectedTrip, index: number): DetectedTrip => {
      const editedTrip = editedTrips.get(index);
      return editedTrip ? ({ ...trip, ...editedTrip } as DetectedTrip) : trip;
    },
    [editedTrips],
  );

  // Trip object used for storing/creating (excluded bookings removed).
  const getEffectiveTrip = useCallback(
    (trip: DetectedTrip, index: number): DetectedTrip => {
      const merged = getDisplayTrip(trip, index);
      const nextBookings = (merged.bookings || []).filter((b, bi) =>
        !isBookingExcluded('new', index, b, bi),
      );
      const nextEmailSubjects = Array.from(
        new Set(nextBookings.map((b) => b.rawEmailSubject).filter((s): s is string => !!s && s.trim().length > 0)),
      );
      return { ...merged, bookings: nextBookings, emailSubjects: nextEmailSubjects };
    },
    [getDisplayTrip, isBookingExcluded],
  );

  const getEffectiveExistingMatchBookings = useCallback(
    (matchIndex: number, bookings: CategorizedDetection['existingTripMatches'][number]['bookings']) => {
      return (bookings || []).filter((b, bi) => !isBookingExcluded('existing', matchIndex, b, bi));
    },
    [isBookingExcluded],
  );

  const minimizedLabel = useMemo(() => {
    if (error) return 'Trip detection failed — tap to view';
    if (loading) {
      if (creationProgress) {
        return `Creating trip ${creationProgress.current} of ${creationProgress.total}…`;
      }
      return 'Creating trips…';
    }
    if (detecting) return detectionProgress ? `Detecting trips… ${detectionProgress}` : 'Detecting trips…';
    const total = trips.length + existingTripMatches.length;
    if (total > 0) return `Detected ${total} result${total !== 1 ? 's' : ''} — tap to review`;
    return 'Trip detection — tap to view';
  }, [creationProgress, detecting, detectionProgress, error, existingTripMatches.length, loading, trips.length]);

  const canMinimize = Boolean(currentTaskIdRef.current) && detecting && (presentation !== 'screen' || onMinimize);
  const handleMinimize = () => {
    if (onMinimize && currentTaskIdRef.current) {
      onMinimize({
        taskId: currentTaskIdRef.current,
        userId,
        provider,
        label: minimizedLabel,
      });
      return;
    }
    setMinimized(true);
  };


  const showMinimizedBanner = visible && minimized;

  const toggleTripSelection = (index: number) => {
    const newSelected = new Set(selectedTrips);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTrips(newSelected);
  };

  const hasAnySelectedBookings = useCallback(() => {
    // New trips
    for (const index of selectedTrips) {
      const trip = trips[index];
      if (!trip) continue;
      if (getEffectiveTrip(trip, index).bookings?.length) return true;
    }
    // Existing trip matches
    for (const matchIndex of selectedExistingMatches) {
      const match = existingTripMatches[matchIndex];
      if (!match) continue;
      if (getEffectiveExistingMatchBookings(matchIndex, match.bookings).length) return true;
    }
    return false;
  }, [existingTripMatches, getEffectiveExistingMatchBookings, getEffectiveTrip, selectedExistingMatches, selectedTrips, trips]);

  const startEditingTrip = (index: number) => {
    setEditingTrip(index);
    const trip = trips[index];
    const edited = editedTrips.get(index) || {};
    setEditedTrips(new Map(editedTrips.set(index, {
      suggestedName: edited.suggestedName || trip.suggestedName,
      startDate: edited.startDate || trip.startDate,
      endDate: edited.endDate || trip.endDate,
      location: edited.location || trip.location,
    })));
  };

  const saveTripEdit = (index: number) => {
    setEditingTrip(null);
  };

  const updateTripField = (index: number, field: keyof DetectedTrip, value: string) => {
    const edited = editedTrips.get(index) || {};
    edited[field] = value as any;
    setEditedTrips(new Map(editedTrips.set(index, edited)));
  };

  const getTripForCreation = (index: number): DetectedTrip => {
    const trip = trips[index];
    return getEffectiveTrip(trip, index);
  };

  const mergeSelectedTrips = () => {
    if (selectedTrips.size < 2) {
      setError('Please select at least 2 trips to merge');
      return;
    }

    const selectedIndices = Array.from(selectedTrips).sort((a, b) => a - b);
    const tripsToMerge = selectedIndices.map(index => getTripForCreation(index));

    const allBookings = tripsToMerge.flatMap(trip => trip.bookings || []);

    const mergedMeta = deriveMergedDetectedTripPresentation({
      bookings: allBookings,
      fallbackFromTripsToMerge: tripsToMerge.map((t) => ({ startDate: t.startDate, endDate: t.endDate })),
      fallbackTripSuggestedNames: tripsToMerge.map((t) => t.suggestedName),
    });

    const mergedTrip: DetectedTrip = {
      suggestedName: mergedMeta.suggestedName,
      startDate: mergedMeta.startDate,
      endDate: mergedMeta.endDate,
      location: mergedMeta.location,
      bookings: allBookings,
      confidence: Math.min(0.9, tripsToMerge.reduce((sum, t) => sum + (t.confidence || 0.5), 0) / tripsToMerge.length),
      emailSubjects: [...new Set(tripsToMerge.flatMap(t => t.emailSubjects || []))],
    };
    
    // Remove merged trips and add the merged one
    const newTrips = trips.filter((_, index) => !selectedIndices.includes(index));
    newTrips.push(mergedTrip);
    setTrips(newTrips);
    // Exclusion keys include parent indices; after merging indices change, so reset exclusions for clarity.
    setExcludedBookingKeys(new Set());
    
    // Select the merged trip
    setSelectedTrips(new Set([newTrips.length - 1]));
    setError(null);
  };

  const toggleExistingMatchSelection = (index: number) => {
    const newSelected = new Set(selectedExistingMatches);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedExistingMatches(newSelected);
  };

  const toggleExistingMatchExpansion = (index: number) => {
    const newExpanded = new Set(expandedExistingMatches);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedExistingMatches(newExpanded);
  };

  const createSelectedTrips = async () => {
    const hasNewTrips = selectedTrips.size > 0;
    const hasExistingMatches = selectedExistingMatches.size > 0;
    
    if (!hasNewTrips && !hasExistingMatches) {
      setError('Please select at least one trip or booking to add');
      return;
    }
    if (!hasAnySelectedBookings()) {
      setError('No bookings selected. Uncheck fewer emails/bookings or select a different trip.');
      return;
    }

    setLoading(true);
    setError(null);
    setFailedTrips([]);
    setCreatedTripIds([]);
    setShowSuccessMessage(false);

    try {
      const selectedNewIndicesAll = Array.from(selectedTrips).sort((a, b) => a - b);
      const selectedNewIndices = isPremium ? selectedNewIndicesAll : selectedNewIndicesAll.slice(0, 1);
      const selectedExistingIndices = Array.from(selectedExistingMatches);
      const totalOperations = selectedNewIndices.length + selectedExistingIndices.length;

      if (!isPremium && selectedNewIndicesAll.length > 1) {
        setLoading(false);
        openPremiumGateAfterDismiss({
          feature: 'email_sync_unlimited',
          title: 'Email import limits',
          message: 'Free users can import bookings into 1 trip. Upgrade to Premium for unlimited email sync and imports.',
          source: 'feature_gate',
        });
        return;
      }

      // Freemium: booking import (email sync) is allowed for 1 trip lifetime on Free.
      if (!isPremium && selectedNewIndices.length > 0) {
        const { allowed } = await consumeLifetimeLimit(userId, 'email_sync_trip', 1);
        if (!allowed) {
          setLoading(false);
          openPremiumGateAfterDismiss({
            feature: 'email_sync_unlimited',
            title: 'Unlimited email sync',
            message:
              'You’ve used the one free email import on this account. It does not reset if you delete a trip. Upgrade to Premium for unlimited email sync and booking capture.',
            source: 'feature_gate',
          });
          return;
        }
      }
      
      let successCount = 0;
      let failCount = 0;
      let existingUpdatedCount = 0;
      const newTripIds: string[] = [];
      const newFailedTrips: Array<{ trip: DetectedTrip; index: number; error: string }> = [];

      // First, add bookings to existing trips
      for (let i = 0; i < selectedExistingIndices.length; i++) {
        const matchIndex = selectedExistingIndices[i];
        const match = existingTripMatches[matchIndex];
        
        if (!match) continue;
        const includedBookings = getEffectiveExistingMatchBookings(matchIndex, match.bookings);
        if (includedBookings.length === 0) continue;
        
        try {
          setCreationProgress({
            current: i + 1,
            total: totalOperations,
            tripName: `Adding to ${match.trip.tripName}...`,
          });
          
          // Store bookings to existing trip
          await storeDetectedBookings(match.trip.tripId, userId, includedBookings, provider, {
            autoExpandTripDates: false,
          });
          console.log(`[DetectedTripsPreview] Added ${includedBookings.length} bookings to existing trip ${match.trip.tripId}`);
          
          existingUpdatedCount++;
        } catch (err) {
          console.error(`[DetectedTripsPreview] Error adding bookings to existing trip:`, err);
          failCount++;
        }
      }

      // Then, create new trips
      for (let i = 0; i < selectedNewIndices.length; i++) {
        const index = selectedNewIndices[i];
        try {
          const sourceTrip = getDisplayTrip(trips[index], index);
          const trip = getTripForCreation(index);
          if (!trip.bookings || trip.bookings.length === 0) {
            // If user excluded all detected bookings for this trip, skip creating an empty trip.
            continue;
          }
          const hadExcludedBookings =
            (sourceTrip.bookings?.length || 0) > (trip.bookings?.length || 0);
          
          // Update progress
          setCreationProgress({
            current: selectedExistingIndices.length + i + 1,
            total: totalOperations,
            tripName: trip.suggestedName,
          });

          const startDate = new Date(trip.startDate);
          const endDate = new Date(trip.endDate);

          // Create trip
          const tripData: CreateTripData = {
            name: trip.suggestedName,
            destination: trip.location,
            startDate,
            endDate,
            travelMode: 'plane', // Default, can be improved
            emailSyncEnabled: true,
            // Inherit currency from user's default currency preference
            baseCurrency: profile.defaultCurrency || 'USD',
          };

          const tripId = await createTrip(userId, tripData);
          newTripIds.push(tripId);

          // Store bookings directly from detected trip (immediate availability)
          if (trip.bookings && trip.bookings.length > 0) {
            try {
              await storeDetectedBookings(tripId, userId, trip.bookings, provider, {
                autoExpandTripDates: false,
              });
              console.log(`[DetectedTripsPreview] Stored ${trip.bookings.length} bookings from detected trip ${tripId}`);
            } catch (storeError) {
              console.warn(`[DetectedTripsPreview] Failed to store detected bookings for trip ${tripId}:`, storeError);
              // Continue even if storing fails - syncInbox will try to catch them
            }
          }

          // Respect explicit deselection: do not auto-sync inbox when user excluded bookings.
          // Otherwise excluded emails can be re-imported into itinerary right after trip creation.
          if (!hadExcludedBookings) {
            try {
              await syncInbox(tripId, provider);
            } catch (syncError) {
              console.warn(`[DetectedTripsPreview] Failed to sync inbox for trip ${tripId}:`, syncError);
              // Continue even if sync fails
            }
          } else {
            console.log(`[DetectedTripsPreview] Skipping auto-sync for trip ${tripId} because user excluded one or more detected bookings`);
          }

          successCount++;
        } catch (err) {
          console.error(`[DetectedTripsPreview] Error creating trip at index ${index}:`, err);
          failCount++;
          const trip = getTripForCreation(index);
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          newFailedTrips.push({ trip, index, error: errorMessage });
        }
      }

      setCreatedTripIds(newTripIds);
      setFailedTrips(newFailedTrips);
      setCreationProgress(null);

      if (newTripIds.length === 0 && existingUpdatedCount === 0 && failCount === 0) {
        setError('No bookings were added. Uncheck fewer emails/bookings or select a different trip.');
        return;
      }

      if (failCount > 0) {
        const successTotal = successCount + existingUpdatedCount;
        setError(`Processed ${successTotal} item${successTotal !== 1 ? 's' : ''}, ${failCount} failed`);
      }

      if (successCount > 0 || existingUpdatedCount > 0) {
        setShowSuccessMessage(true);
        // Auto-close after showing success, or let user close manually
        setTimeout(() => {
          onTripsCreated();
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('[DetectedTripsPreview] Error creating trips:', err);
      setError(err instanceof Error ? err.message : 'Failed to create trips');
    } finally {
      setLoading(false);
      setCreationProgress(null);
    }
  };

  const retryFailedTrip = async (failedTrip: { trip: DetectedTrip; index: number; error: string }) => {
    setLoading(true);
    setError(null);

    try {
      const trip = failedTrip.trip;
      const startDate = new Date(trip.startDate);
      const endDate = new Date(trip.endDate);

      const tripData: CreateTripData = {
        name: trip.suggestedName,
        destination: trip.location,
        startDate,
        endDate,
        travelMode: 'plane',
        emailSyncEnabled: true,
        // Inherit currency from user's default currency preference
        baseCurrency: profile.defaultCurrency || 'USD',
      };

      const tripId = await createTrip(userId, tripData);
      setCreatedTripIds(prev => [...prev, tripId]);

      if (trip.bookings && trip.bookings.length > 0) {
        try {
          await storeDetectedBookings(tripId, userId, trip.bookings, provider, {
            autoExpandTripDates: false,
          });
        } catch (storeError) {
          console.warn(`[DetectedTripsPreview] Failed to store detected bookings for trip ${tripId}:`, storeError);
        }
      }

      // Retry path preserves the selected detected payload only.
      // Avoid immediate inbox auto-sync because it may re-import intentionally excluded emails.

      // Remove from failed list
      setFailedTrips(prev => prev.filter(ft => ft.index !== failedTrip.index));
      setShowSuccessMessage(true);
    } catch (err) {
      console.error('[DetectedTripsPreview] Error retrying trip creation:', err);
      setError(err instanceof Error ? err.message : 'Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  const stripHtml = (value?: string) =>
    value ? value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : value;

  const clean = (value?: string) => stripHtml(value) || value;

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getBookingTypeIcon = (type: string): keyof typeof Ionicons.glyphMap => {
    switch (type) {
      case 'flight': return 'airplane';
      case 'hotel': return 'bed';
      case 'car': return 'car';
      case 'restaurant': return 'restaurant';
      case 'activity': return 'ticket';
      default: return 'calendar';
    }
  };

  const toggleTripExpansion = (index: number) => {
    const newExpanded = new Set(expandedTrips);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTrips(newExpanded);
  };


  const renderSheet = () => (
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            flex: 1,
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              paddingTop: insets.top + theme.spacing(1.5),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <SubHeading>Detected Trips</SubHeading>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              {canMinimize && (
                <Pressable
                  onPress={handleMinimize}
                  accessibilityRole="button"
                  accessibilityLabel="Minimize detected trips"
                  accessibilityHint="Hides this window while detection continues"
                  style={{
                    padding: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.background,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                  }}
                >
                  <Ionicons name="remove" size={20} color={theme.colors.textSecondary} />
                </Pressable>
              )}
              <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Close detected trips">
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>
          </View>

          {/* Content */}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: theme.spacing(2),
              paddingBottom: insets.bottom + theme.spacing(2),
            }}
          >
            {scanLookbackDays !== null && (
              <View style={{ marginBottom: theme.spacing(2), gap: theme.spacing(1) }}>
                <Caption color={theme.colors.textSecondary} style={{ lineHeight: 20 }}>
                  {emailScanArrivalWindowForScan(scanLookbackDays)}
                </Caption>
                <Caption color={theme.colors.textSecondary} style={{ lineHeight: 20 }}>
                  {emailOlderConfirmationsHint()}
                </Caption>
              </View>
            )}

            {/* Creation Progress */}
            {loading && creationProgress && (
              <Card variant="outlined" style={{ marginBottom: theme.spacing(2), borderColor: theme.colors.primary }}>
                <View style={{ gap: theme.spacing(1.5) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <View style={{ flex: 1 }}>
                      <Body>
                        Creating trip {creationProgress.current} of {creationProgress.total}...
                      </Body>
                      {creationProgress.tripName && (
                        <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                          {creationProgress.tripName}
                        </Caption>
                      )}
                    </View>
                  </View>
                  {/* Progress Bar */}
                  <View
                    style={{
                      height: 4,
                      backgroundColor: theme.colors.border,
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <View
                      style={{
                        height: '100%',
                        width: `${(creationProgress.current / creationProgress.total) * 100}%`,
                        backgroundColor: theme.colors.primary,
                        borderRadius: 2,
                      }}
                    />
                  </View>
                </View>
              </Card>
            )}

            {/* Success Message */}
            {showSuccessMessage && createdTripIds.length > 0 && (
              <Card variant="outlined" style={{ marginBottom: theme.spacing(2), borderColor: theme.colors.success }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                  <Body style={{ color: theme.colors.success }}>
                    Successfully created {createdTripIds.length} trip{createdTripIds.length !== 1 ? 's' : ''}!
                  </Body>
                </View>
              </Card>
            )}

            {/* Failed Trips with Retry */}
            {failedTrips.length > 0 && (
              <Card variant="outlined" style={{ marginBottom: theme.spacing(2), borderColor: theme.colors.error }}>
                <View style={{ gap: theme.spacing(1.5) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
                    <SubHeading style={{ color: theme.colors.error }}>
                      {failedTrips.length} Trip{failedTrips.length !== 1 ? 's' : ''} Failed
                    </SubHeading>
                  </View>
                  {failedTrips.map((failedTrip, idx) => (
                    <View key={idx} style={{ padding: theme.spacing(1.5), backgroundColor: theme.colors.background, borderRadius: theme.radii.md }}>
                      <Body style={{ fontWeight: '600', marginBottom: theme.spacing(0.5) }}>
                        {failedTrip.trip.suggestedName}
                      </Body>
                      <Caption color={theme.colors.error} style={{ marginBottom: theme.spacing(1) }}>
                        {failedTrip.error}
                      </Caption>
                      <Button
                        label="Retry"
                        onPress={() => retryFailedTrip(failedTrip)}
                        variant="outlined"
                        size="small"
                        disabled={loading}
                      />
                    </View>
                  ))}
                </View>
              </Card>
            )}

            {detecting ? (
              <View style={{ alignItems: 'center', padding: theme.spacing(4) }}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Body style={{ marginTop: theme.spacing(2), color: theme.colors.textSecondary }}>
                  {detectionProgress || 'Detecting trips from your emails...'}
                </Body>
                {quickScanDone === false && (
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1), textAlign: 'center' }}>
                    First scan may take a few minutes.
                  </Caption>
                )}
              </View>
            ) : error && trips.length === 0 ? (
              <View style={{ padding: theme.spacing(2) }}>
                <Card variant="outlined" style={{ padding: theme.spacing(2) }}>
                  <Body style={{ color: theme.colors.error }}>{error}</Body>
                  <Button
                    label="Try Again"
                    onPress={() => {
                      void detectTrips();
                    }}
                    style={{ marginTop: theme.spacing(2) }}
                  />
                </Card>
              </View>
            ) : trips.length === 0 && existingTripMatches.length === 0 ? (
              <View style={{ padding: theme.spacing(2) }}>
                <Card variant="outlined" style={{ padding: theme.spacing(2) }}>
                  <Body style={{ color: theme.colors.textSecondary }}>
                    No trips detected. Make sure you have booking emails in your inbox.
                  </Body>
                </Card>
              </View>
            ) : (
              <>
                {lastScanWasQuick && (
                  <Card variant="outlined" style={{ marginBottom: theme.spacing(2) }}>
                    <View style={{ gap: theme.spacing(1) }}>
                      <Body style={{ fontWeight: '600' }}>Want more results?</Body>
                      <Caption color={theme.colors.textSecondary} style={{ lineHeight: 20 }}>
                        {emailScanDeeperScanTeaser(FULL_SCAN_LOOKBACK_DAYS, QUICK_SCAN_LOOKBACK_DAYS)}
                      </Caption>
                      <Button
                        label="Scan more emails"
                        onPress={() => detectTrips({ forceFullScan: true })}
                        variant="outlined"
                        disabled={detecting || loading}
                      />
                    </View>
                  </Card>
                )}
                {/* Existing Trip Matches Section */}
                {existingTripMatches.length > 0 && (
                  <View style={{ marginBottom: theme.spacing(3) }}>
                    <View style={{ 
                      flexDirection: 'row', 
                      alignItems: 'center', 
                      gap: theme.spacing(1), 
                      marginBottom: theme.spacing(1.5),
                      paddingBottom: theme.spacing(1),
                      borderBottomWidth: 1,
                      borderBottomColor: theme.colors.borderMuted,
                    }}>
                      <Ionicons name="link" size={20} color={theme.colors.success} />
                      <SubHeading style={{ color: theme.colors.success }}>
                        Add to Existing Trips
                      </SubHeading>
                    </View>
                    <Body style={{ color: theme.colors.textSecondary, marginBottom: theme.spacing(2) }}>
                      {`Found ${existingTripMatches.reduce((sum, m) => sum + m.bookings.length, 0)} booking${existingTripMatches.reduce((sum, m) => sum + m.bookings.length, 0) !== 1 ? 's' : ''} that match your existing trips.`}
                    </Body>
                    
                    {existingTripMatches.map((match, index) => {
                      const needsReview = match.bookings.some((b) => !b.location);
                      const isSelected = selectedExistingMatches.has(index);
                      const isExpanded = expandedExistingMatches.has(index);
                      
                      return (
                        <Card
                          key={`existing-${index}`}
                          variant={isSelected ? 'filled' : 'outlined'}
                          style={{
                            marginBottom: theme.spacing(2),
                            borderColor: isSelected ? theme.colors.success : theme.colors.borderMuted,
                            borderWidth: isSelected ? 2 : 1,
                          }}
                        >
                          <Pressable onPress={() => toggleExistingMatchSelection(index)}>
                            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(2) }}>
                              {/* Checkbox */}
                              <View
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 4,
                                  borderWidth: 2,
                                  borderColor: isSelected ? theme.colors.success : theme.colors.borderMuted,
                                  backgroundColor: isSelected ? theme.colors.success : 'transparent',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginTop: 2,
                                }}
                              >
                                {isSelected && (
                                  <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                                )}
                              </View>
                              
                              {/* Match Info */}
                              <View style={{ flex: 1 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                                  <Ionicons name="add-circle" size={18} color={theme.colors.success} />
                                  <Body style={{ fontWeight: '600' }}>
                                    {match.bookings.length} booking{match.bookings.length !== 1 ? 's' : ''} → 
                                  </Body>
                                </View>
                                <SubHeading style={{ marginTop: 4 }}>
                                  {match.trip.tripName}
                                </SubHeading>
                                <View style={{ flexDirection: 'row', gap: theme.spacing(2), marginTop: 4 }}>
                                  <Caption style={{ color: theme.colors.textSecondary }}>
                                    {formatDate(match.trip.startDate)} - {formatDate(match.trip.endDate)}
                                  </Caption>
                                </View>
                              {needsReview && (
                                <Caption style={{ color: theme.colors.warning, marginTop: 2 }}>
                                  Needs review: some bookings are missing a location
                                </Caption>
                              )}
                                {match.trip.destination && (
                                  <Caption style={{ color: theme.colors.textSecondary, marginTop: 2 }}>
                                    📍 {match.trip.destination}
                                  </Caption>
                                )}
                                
                                {/* Expand/Collapse bookings */}
                                <Pressable
                                  onPress={() => toggleExistingMatchExpansion(index)}
                                  style={{ marginTop: theme.spacing(1.5) }}
                                >
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                                    <Ionicons
                                      name={isExpanded ? 'chevron-up' : 'chevron-down'}
                                      size={16}
                                      color={theme.colors.success}
                                    />
                                    <Caption style={{ color: theme.colors.success }}>
                                      {isExpanded ? 'Hide' : 'View'} {match.bookings.length} booking{match.bookings.length !== 1 ? 's' : ''}
                                    </Caption>
                                  </View>
                                </Pressable>
                                
                                {isExpanded && (
                                  <View style={{ marginTop: theme.spacing(1.5), padding: theme.spacing(1.5), backgroundColor: theme.colors.background, borderRadius: theme.radii.md }}>
                                    {match.bookings.map((booking, bookingIndex) => (
                                      <View
                                        key={bookingIndex}
                                        style={{
                                          padding: theme.spacing(1.5),
                                          marginBottom: theme.spacing(1),
                                          backgroundColor: theme.colors.surface,
                                          borderRadius: theme.radii.sm,
                                          borderLeftWidth: 3,
                                          borderLeftColor: theme.colors.success,
                                        }}
                                      >
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), marginBottom: theme.spacing(0.5) }}>
                                          <Ionicons
                                            name={getBookingTypeIcon(booking.type)}
                                            size={16}
                                            color={theme.colors.success}
                                          />
                                          <View style={{ flex: 1, minWidth: 0 }}>
                                            <Body
                                              style={{ fontWeight: '600', textTransform: 'capitalize' }}
                                              numberOfLines={1}
                                              ellipsizeMode="tail"
                                            >
                                              {booking.type} - {clean(booking.provider)}
                                            </Body>
                                          </View>
                                        </View>
                                        {booking.rawEmailSubject && (
                                          <Caption
                                            style={{ fontSize: 11, marginTop: theme.spacing(0.5), fontStyle: 'italic' }}
                                            numberOfLines={2}
                                            ellipsizeMode="tail"
                                          >
                                            {booking.rawEmailSubject}
                                          </Caption>
                                        )}
                                        {booking.confirmationNumber && (
                                          <Caption style={{ fontSize: 11, marginTop: theme.spacing(0.5) }} numberOfLines={1} ellipsizeMode="tail">
                                            Confirmation: {booking.confirmationNumber}
                                          </Caption>
                                        )}
                                        {booking.location && (
                                          <Caption style={{ fontSize: 11, marginTop: theme.spacing(0.5) }} numberOfLines={1} ellipsizeMode="tail">
                                            📍 {booking.location}
                                          </Caption>
                                        )}
                                      </View>
                                    ))}
                                  </View>
                                )}
                              </View>
                            </View>
                          </Pressable>
                        </Card>
                      );
                    })}
                  </View>
                )}
                
                {/* New Trips Section */}
                {trips.length > 0 && (
                <View style={{ marginBottom: theme.spacing(2) }}>
                    {existingTripMatches.length > 0 && (
                      <View style={{ 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        gap: theme.spacing(1), 
                        marginBottom: theme.spacing(1.5),
                        paddingBottom: theme.spacing(1),
                        borderBottomWidth: 1,
                        borderBottomColor: theme.colors.borderMuted,
                      }}>
                        <Ionicons name="add" size={20} color={theme.colors.primary} />
                        <SubHeading style={{ color: theme.colors.primary }}>
                          Create New Trips
                        </SubHeading>
                      </View>
                    )}
                    <Body style={{ color: theme.colors.textSecondary, marginBottom: theme.spacing(2) }}>
                      {`Found ${trips.length} new trip${trips.length !== 1 ? 's' : ''} from your emails. Select the ones you want to create.`}
                  </Body>
                </View>
                )}

                {trips.map((trip, index) => {
                  const isSelected = selectedTrips.has(index);
                  const isEditing = editingTrip === index;
                  const displayTrip = getDisplayTrip(trip, index);
                  const effectiveTrip = getEffectiveTrip(trip, index);

                  return (
                    <Card
                      key={index}
                      variant={isSelected ? 'filled' : 'outlined'}
                      style={{
                        marginBottom: theme.spacing(2),
                        borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                        borderWidth: isSelected ? 2 : 1,
                      }}
                    >
                      <Pressable onPress={() => toggleTripSelection(index)}>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: theme.spacing(2),
                          }}
                        >
                          {/* Checkbox */}
                          <View
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 4,
                              borderWidth: 2,
                              borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                              backgroundColor: isSelected ? theme.colors.primary : 'transparent',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: 2,
                            }}
                          >
                            {isSelected && (
                              <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                            )}
                          </View>

                          {/* Trip Info */}
                          <View style={{ flex: 1 }}>
                            {isEditing ? (
                              <View style={{ gap: theme.spacing(1.5) }}>
                                <TextInput
                                  value={displayTrip.suggestedName}
                                  onChangeText={(text) => updateTripField(index, 'suggestedName', text)}
                                  placeholder="Trip name"
                                  style={{
                                    borderWidth: 1,
                                    borderColor: theme.colors.borderMuted,
                                    borderRadius: theme.radii.md,
                                    padding: theme.spacing(1.5),
                                    color: theme.colors.text,
                                    backgroundColor: theme.colors.background,
                                  }}
                                />
                                <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                                  <TextInput
                                    value={displayTrip.startDate}
                                    onChangeText={(text) => updateTripField(index, 'startDate', text)}
                                    placeholder="Start date (YYYY-MM-DD)"
                                    style={{
                                      flex: 1,
                                      borderWidth: 1,
                                      borderColor: theme.colors.borderMuted,
                                      borderRadius: theme.radii.md,
                                      padding: theme.spacing(1.5),
                                      color: theme.colors.text,
                                      backgroundColor: theme.colors.background,
                                    }}
                                  />
                                  <TextInput
                                    value={displayTrip.endDate}
                                    onChangeText={(text) => updateTripField(index, 'endDate', text)}
                                    placeholder="End date (YYYY-MM-DD)"
                                    style={{
                                      flex: 1,
                                      borderWidth: 1,
                                      borderColor: theme.colors.borderMuted,
                                      borderRadius: theme.radii.md,
                                      padding: theme.spacing(1.5),
                                      color: theme.colors.text,
                                      backgroundColor: theme.colors.background,
                                    }}
                                  />
                                </View>
                                <TextInput
                                  id={`trip-location-${index}`}
                                  name={`trip-location-${index}`}
                                  value={displayTrip.location || ''}
                                  onChangeText={(text) => updateTripField(index, 'location', text)}
                                  placeholder="Location"
                                  style={{
                                    borderWidth: 1,
                                    borderColor: theme.colors.borderMuted,
                                    borderRadius: theme.radii.md,
                                    padding: theme.spacing(1.5),
                                    color: theme.colors.text,
                                    backgroundColor: theme.colors.background,
                                  }}
                                />
                                <Button
                                  label="Save"
                                  onPress={() => saveTripEdit(index)}
                                  variant="primary"
                                />
                              </View>
                            ) : (
                              <>
                                <SubHeading>{displayTrip.suggestedName}</SubHeading>
                                <View style={{ flexDirection: 'row', gap: theme.spacing(2), marginTop: 4 }}>
                                  <Caption style={{ color: theme.colors.textSecondary }}>
                                    {formatDate(displayTrip.startDate)} - {formatDate(displayTrip.endDate)}
                                  </Caption>
                                </View>
                                {displayTrip.location && (
                                  <Caption style={{ color: theme.colors.textSecondary, marginTop: 2 }} numberOfLines={1} ellipsizeMode="tail">
                                    📍 {displayTrip.location}
                                  </Caption>
                                )}
                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(1) }}>
                                  {trip.bookings.map((booking, bookingIndex) => (
                                    <View
                                      key={bookingIndex}
                                      style={{
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 4,
                                        paddingHorizontal: 8,
                                        paddingVertical: 4,
                                        borderRadius: 12,
                                        backgroundColor: theme.colors.background,
                                      }}
                                    >
                                      <Ionicons
                                        name={getBookingTypeIcon(booking.type)}
                                        size={12}
                                        color={theme.colors.primary}
                                      />
                                      <Caption style={{ fontSize: 11 }} numberOfLines={1} ellipsizeMode="tail">
                                        {clean(booking.provider)}
                                      </Caption>
                                    </View>
                                  ))}
                                </View>
                                
                                {/* Expandable bookings section */}
                                {(trip.emailSubjects && trip.emailSubjects.length > 0) || (trip.bookings && trip.bookings.length > 0) ? (
                                  <Pressable
                                    onPress={() => toggleTripExpansion(index)}
                                    style={{ marginTop: theme.spacing(1.5) }}
                                  >
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                                      <Ionicons
                                        name={expandedTrips.has(index) ? 'chevron-up' : 'chevron-down'}
                                        size={16}
                                        color={theme.colors.primary}
                                      />
                                      <Caption style={{ color: theme.colors.primary }}>
                                        {expandedTrips.has(index) ? 'Hide' : 'View'} {effectiveTrip.bookings?.length || 0} booking{(effectiveTrip.bookings?.length || 0) !== 1 ? 's' : ''}
                                      </Caption>
                                    </View>
                                  </Pressable>
                                ) : null}

                                {expandedTrips.has(index) && (
                                  <View style={{ marginTop: theme.spacing(1.5), padding: theme.spacing(1.5), backgroundColor: theme.colors.background, borderRadius: theme.radii.md }}>
                                    {/* Booking Details */}
                                    {displayTrip.bookings && displayTrip.bookings.length > 0 && (
                                      <View>
                                        <Body style={{ fontWeight: '600', marginBottom: theme.spacing(0.5) }}>
                                          Booking Details:
                                        </Body>
                                        {displayTrip.bookings.map((booking, bookingIndex) => {
                                          const excluded = isBookingExcluded('new', index, booking, bookingIndex);
                                          return (
                                          <View
                                            key={bookingIndex}
                                            style={{
                                              padding: theme.spacing(1.5),
                                              marginBottom: theme.spacing(1),
                                              backgroundColor: theme.colors.surface,
                                              borderRadius: theme.radii.sm,
                                              borderLeftWidth: 3,
                                              borderLeftColor: theme.colors.primary,
                                              opacity: excluded ? 0.55 : 1,
                                            }}
                                          >
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), marginBottom: theme.spacing(0.5) }}>
                                              <Pressable
                                                onPress={(e: any) => {
                                                  e?.stopPropagation?.();
                                                  toggleBookingExcluded('new', index, booking, bookingIndex);
                                                }}
                                                accessibilityRole="checkbox"
                                                accessibilityLabel={excluded ? 'Include this email/booking' : 'Exclude this email/booking'}
                                                accessibilityState={{ checked: !excluded }}
                                                style={({ pressed }) => ({
                                                  padding: theme.spacing(0.5),
                                                  borderRadius: 999,
                                                  opacity: pressed ? 0.8 : 1,
                                                })}
                                              >
                                                <Ionicons
                                                  name={excluded ? 'square-outline' : 'checkbox'}
                                                  size={18}
                                                  color={excluded ? theme.colors.textSecondary : theme.colors.primary}
                                                />
                                              </Pressable>
                                              <Ionicons
                                                name={getBookingTypeIcon(booking.type)}
                                                size={16}
                                                color={theme.colors.primary}
                                              />
                                              <View style={{ flex: 1, minWidth: 0 }}>
                                                <Body
                                                  style={{ fontWeight: '600', textTransform: 'capitalize' }}
                                                  numberOfLines={1}
                                                  ellipsizeMode="tail"
                                                >
                                                  {booking.type} - {clean(booking.provider)}
                                                </Body>
                                              </View>
                                            </View>
                                            {showEmailSubjects && booking.rawEmailSubject && (
                                              <Caption
                                                style={{ fontSize: 11, marginTop: theme.spacing(0.5), fontStyle: 'italic' }}
                                                numberOfLines={2}
                                                ellipsizeMode="tail"
                                              >
                                                {clean(booking.rawEmailSubject)}
                                              </Caption>
                                            )}
                                            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, marginTop: theme.spacing(0.5) }}>
                                              {excluded ? 'Excluded — will not be added' : 'Included'}
                                            </Caption>
                                            {booking.confirmationNumber && (
                                              <Caption style={{ fontSize: 11, marginTop: theme.spacing(0.5) }} numberOfLines={1} ellipsizeMode="tail">
                                                Confirmation: {booking.confirmationNumber}
                                              </Caption>
                                            )}
                                            {booking.location && (
                                              <Caption style={{ fontSize: 11, marginTop: theme.spacing(0.5) }} numberOfLines={1} ellipsizeMode="tail">
                                                📍 {clean(booking.location)}
                                              </Caption>
                                            )}
                                            {(booking.departureDate || booking.checkInDate) && (
                                              <Caption style={{ fontSize: 11, marginTop: theme.spacing(0.5) }}>
                                                {booking.departureDate 
                                                  ? `Departure: ${formatDate(booking.departureDate)}`
                                                  : booking.checkInDate 
                                                    ? `Check-in: ${formatDate(booking.checkInDate)}`
                                                    : null}
                                                {booking.arrivalDate && ` - Arrival: ${formatDate(booking.arrivalDate)}`}
                                                {booking.checkOutDate && ` - Check-out: ${formatDate(booking.checkOutDate)}`}
                                              </Caption>
                                            )}
                                          </View>
                                        );
                                        })}
                                      </View>
                                    )}
                                  </View>
                                )}

                                <Pressable
                                  onPress={() => startEditingTrip(index)}
                                  style={{ marginTop: theme.spacing(1) }}
                                >
                                  <Caption style={{ color: theme.colors.primary }}>
                                    ✏️ Edit
                                  </Caption>
                                </Pressable>
                              </>
                            )}
                          </View>
                        </View>
                      </Pressable>
                    </Card>
                  );
                })}
              </>
            )}

            {error && trips.length > 0 && (
              <Card variant="outlined" style={{ padding: theme.spacing(2), marginTop: theme.spacing(2) }}>
                <Body style={{ color: theme.colors.error }}>{error}</Body>
              </Card>
            )}
          </ScrollView>

          {/* Footer Actions */}
          {(trips.length > 0 || existingTripMatches.length > 0) && (
            <View
              style={{
                padding: theme.spacing(2),
                borderTopWidth: 1,
                borderTopColor: theme.colors.borderMuted,
                gap: theme.spacing(1.5),
              }}
            >
              {/* Selection summary */}
              <View style={{ gap: theme.spacing(1) }}>
                {existingTripMatches.length > 0 && (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                      <Ionicons name="link" size={16} color={theme.colors.success} />
                      <Body style={{ color: theme.colors.success }}>
                        {selectedExistingMatches.size} existing trip{selectedExistingMatches.size !== 1 ? 's' : ''} to update
                      </Body>
                    </View>
                    <Pressable onPress={() => {
                      if (selectedExistingMatches.size === existingTripMatches.length) {
                        setSelectedExistingMatches(new Set());
                      } else {
                        setSelectedExistingMatches(new Set(existingTripMatches.map((_, i) => i)));
                      }
                    }}>
                      <Body style={{ color: theme.colors.success, fontSize: 13 }}>
                        {selectedExistingMatches.size === existingTripMatches.length ? 'Deselect' : 'Select All'}
                      </Body>
                    </Pressable>
                  </View>
                )}
                
                {trips.length > 0 && (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                      <Ionicons name="add-circle" size={16} color={theme.colors.primary} />
                <Body>
                        {selectedTrips.size} new trip{selectedTrips.size !== 1 ? 's' : ''} to create
                </Body>
                    </View>
                <Pressable onPress={() => {
                  if (selectedTrips.size === trips.length) {
                    setSelectedTrips(new Set());
                  } else {
                    setSelectedTrips(new Set(trips.map((_, i) => i)));
                  }
                }}>
                      <Body style={{ color: theme.colors.primary, fontSize: 13 }}>
                        {selectedTrips.size === trips.length ? 'Deselect' : 'Select All'}
                  </Body>
                </Pressable>
              </View>
                )}
              </View>
              
              {selectedTrips.size >= 2 && (
                <Button
                  label="Merge Selected Trips"
                  onPress={mergeSelectedTrips}
                  disabled={loading}
                  variant="outlined"
                  style={{ marginBottom: theme.spacing(1) }}
                />
              )}
              <Button
                label={loading 
                  ? 'Processing...' 
                  : (() => {
                      const parts = [];
                      if (selectedTrips.size > 0) {
                        parts.push(`Create ${selectedTrips.size} Trip${selectedTrips.size !== 1 ? 's' : ''}`);
                      }
                      if (selectedExistingMatches.size > 0) {
                        parts.push(`Update ${selectedExistingMatches.size} Existing`);
                      }
                      return parts.length > 0 ? parts.join(' & ') : 'Select items to proceed';
                    })()
                }
                onPress={createSelectedTrips}
                disabled={loading || (selectedTrips.size === 0 && selectedExistingMatches.size === 0)}
                variant="primary"
              />
            </View>
          )}
        </View>
      </View>
  );

  const minimizedOverlay =
    showMinimizedBanner ? (
      <Portal hostName="floating-overlay">
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            left: theme.spacing(2),
            right: theme.spacing(2),
            bottom: (insets.bottom || 0) + 76,
            zIndex: 999,
            elevation: 20,
          }}
        >
          <Pressable
            onPress={() => setMinimized(false)}
            accessibilityRole="button"
            accessibilityLabel="Trip detection in progress"
            accessibilityHint="Tap to reopen detection details"
            style={({ pressed }) => ({
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              borderRadius: theme.radii.lg,
              padding: theme.spacing(1.5),
              opacity: pressed ? 0.9 : 1,
              shadowColor: '#000',
              shadowOpacity: 0.18,
              shadowRadius: 14,
              shadowOffset: { width: 0, height: 10 },
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1, minWidth: 0 }}>
                <Ionicons
                  name={error ? 'alert-circle-outline' : detecting ? 'sparkles-outline' : 'checkmark-circle-outline'}
                  size={20}
                  color={error ? theme.colors.error : detecting ? theme.colors.primary : (theme.colors.success || theme.colors.primary)}
                />
                <ThemedText variant="bodyStrong" style={{ flex: 1, minWidth: 0, fontSize: 14 }} numberOfLines={1}>
                  {minimizedLabel}
                </ThemedText>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="chevron-up" size={18} color={theme.colors.textSecondary} />
                <Pressable
                  onPress={() => {
                    setMinimized(false);
                    onClose();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss trip detection"
                  accessibilityHint="Closes the trip detection window"
                  style={({ pressed }) => ({
                    padding: theme.spacing(0.5),
                    borderRadius: 999,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Ionicons name="close" size={18} color={theme.colors.textSecondary} />
                </Pressable>
              </View>
            </View>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5), fontSize: 11 }}>
              Tap to view details • You can keep using the app
            </Caption>
          </Pressable>
        </View>
      </Portal>
    ) : null;

  if (presentation === 'screen') {
    return (
      <View
        style={{
          flex: 1,
          width: '100%',
          alignSelf: 'stretch',
          backgroundColor: theme.colors.background,
        }}
      >
        {renderSheet()}
      </View>
    );
  }

  if (presentation === 'embedded') {
    const minBodyHeight = Math.max(320, Math.round(windowHeight * 0.88));
    return (
      <View
        style={{
          flex: 1,
          width: '100%',
          alignSelf: 'stretch',
          minHeight: 0,
          backgroundColor: theme.colors.background,
        }}
      >
        {minimizedOverlay}
        {visible && !minimized ? (
          <View
            style={{
              flex: 1,
              minHeight: minBodyHeight,
              backgroundColor: theme.colors.surface,
            }}
          >
            {renderSheet()}
          </View>
        ) : visible && minimized ? (
          <View style={{ flex: 1, minHeight: minBodyHeight, backgroundColor: theme.colors.background }} />
        ) : null}
      </View>
    );
  }

  return (
    <>
      {minimizedOverlay}
      <Modal
        visible={visible && !minimized}
        animationType="slide"
        onRequestClose={onClose}
        presentationStyle="pageSheet"
        statusBarTranslucent={Platform.OS === 'android'}
      >
        {renderSheet()}
      </Modal>
    </>
  );
}

